import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Pagination } from './ui/pagination';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { ArrowLeft, Download, Calendar, Loader2, ArrowUpDown, ArrowUp, ArrowDown, Search } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell } from 'recharts';
import { toast } from 'sonner';
import { useBranch } from '../contexts/BranchContext';
import { useAuth } from '../contexts/AuthContext';
import { productionReportApiService, ProductionSummaryReportDto, ProductionSummaryTableDto } from '../services/productionReportApi';
import { getPaginationConfig } from '../config/uiConfig';

const ProductionReportPage: React.FC = () => {
  const navigate = useNavigate();
  const { selectedBranch } = useBranch();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState<ProductionSummaryReportDto[]>([]);
  const [summaryData, setSummaryData] = useState<ProductionSummaryTableDto[]>([]);

  // Date range state - default to current month
  const now = new Date();
  const getCurrentMonthStart = () => {
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    return `${year}-${month}-01`;
  };

  // Get today's date in YYYY-MM-DD format for max date validation and default end date
  const getTodayDate = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const [startDate, setStartDate] = useState<string>(getCurrentMonthStart());
  const [endDate, setEndDate] = useState<string>(getTodayDate());

  // Search state
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>('');

  // Pagination state
  const paginationConfig = getPaginationConfig();
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(paginationConfig.defaultItemsPerPage);

  // Sorting state
  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  // Debounce search term - wait 800ms after user stops typing
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 800);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Only load data when branch changes, not when dates or search change
  useEffect(() => {
    if (selectedBranch || user?.defaultBranchId) {
      loadReportData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBranch, user?.defaultBranchId]);

  const loadReportData = async () => {
    const branchId = selectedBranch || user?.defaultBranchId;
    
    if (!branchId) {
      toast.error('Please select a branch to view the production report');
      return;
    }

    if (!startDate || !endDate) {
      toast.error('Please select both start and end dates');
      return;
    }

    if (new Date(startDate) > new Date(endDate)) {
      toast.error('Start date cannot be after end date');
      return;
    }
    
    setLoading(true);
    try {
      const search = debouncedSearchTerm || undefined;
      const [detailResult, summaryResult] = await Promise.allSettled([
        productionReportApiService.getProductionSummaryByDateRange(branchId, startDate, endDate, search),
        productionReportApiService.getSummaryTableByBranchAndDateRange(branchId, startDate, endDate, search),
      ]);

      if (detailResult.status === 'fulfilled' && detailResult.value.success && detailResult.value.data) {
        setReportData(Array.isArray(detailResult.value.data) ? detailResult.value.data : []);
        setCurrentPage(1);
      } else {
        if (detailResult.status === 'fulfilled') {
          toast.error(detailResult.value.message || 'Failed to load production report');
        }
        setReportData([]);
      }

      if (summaryResult.status === 'fulfilled' && summaryResult.value.success && summaryResult.value.data) {
        setSummaryData(Array.isArray(summaryResult.value.data) ? summaryResult.value.data : []);
      } else {
        setSummaryData([]);
      }
    } catch (error) {
      // console.error('Error loading production report:', error);
      setReportData([]);
      setSummaryData([]);
    } finally {
      setLoading(false);
    }
  };

  // Sort data (search is now handled by the API)
  const sortedData = useMemo(() => {
    if (!sortColumn) return reportData;

    return [...reportData].sort((a, b) => {
      const aValue = (a as any)[sortColumn];
      const bValue = (b as any)[sortColumn];

      if (aValue === null || aValue === undefined) return 1;
      if (bValue === null || bValue === undefined) return -1;

      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
      }

      const aStr = String(aValue).toLowerCase();
      const bStr = String(bValue).toLowerCase();

      if (sortDirection === 'asc') {
        return aStr.localeCompare(bStr);
      } else {
        return bStr.localeCompare(aStr);
      }
    });
  }, [reportData, sortColumn, sortDirection]);

  // Pagination calculations
  const totalPages = Math.ceil(sortedData.length / itemsPerPage) || 1;
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedData = sortedData.slice(startIndex, endIndex);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleItemsPerPageChange = (value: string) => {
    setItemsPerPage(parseInt(value));
    setCurrentPage(1);
  };


  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const getSortIcon = (column: string) => {
    if (sortColumn !== column) {
      return <ArrowUpDown className="h-4 w-4 text-gray-400" />;
    }
    return sortDirection === 'asc' 
      ? <ArrowUp className="h-4 w-4 text-primary-600" />
      : <ArrowDown className="h-4 w-4 text-primary-600" />;
  };

  const formatNumber = (value: number | undefined | null): string => {
    if (value === null || value === undefined) return 'N/A';
    return value.toLocaleString('en-IN', { 
      minimumFractionDigits: 0, 
      maximumFractionDigits: 2 
    });
  };

  // Filter and prepare blanking process data for chart
  const blankingChartData = useMemo(() => {
    // Filter data for blanking process
    const blankingData = reportData.filter(item => 
      item.processName && item.processName.toLowerCase().includes('blanking')
    );

    if (blankingData.length === 0) return [];

    // Parse start and end dates
    const start = new Date(startDate);
    const end = new Date(endDate);
    
    // Calculate number of days in range
    const diffTime = Math.abs(end.getTime() - start.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
    
    // Initialize array for all days in range with 0 values
    const showMonth = diffDays > 31 || start.getMonth() !== end.getMonth();
    const daysData = Array.from({ length: diffDays }, (_, i) => {
      const currentDate = new Date(start);
      currentDate.setDate(start.getDate() + i);
      const day = currentDate.getDate();
      // Show month/day if range spans multiple months, otherwise just day
      const dateLabel = showMonth 
        ? currentDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
        : day.toString();
      const dateKey = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(currentDate.getDate()).padStart(2, '0')}`;
      return {
        day: day,
        date: dateLabel,
        fullDate: currentDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
        productionQty: 0,
        rejectQty: 0,
        dateObj: currentDate,
        dateKey,
      };
    });

    // Process blanking data and aggregate by day
    blankingData.forEach((item) => {
      if (item.productionDate) {
        try {
          const itemDate = new Date(item.productionDate);
          itemDate.setHours(0, 0, 0, 0);
          
          // Find the index of the day in the daysData array
          const dayIndex = daysData.findIndex(d => {
            if (!d.dateObj) return false;
            const dDateOnly = new Date(d.dateObj.getFullYear(), d.dateObj.getMonth(), d.dateObj.getDate());
            return dDateOnly.getTime() === itemDate.getTime();
          });
          
          if (dayIndex >= 0 && dayIndex < diffDays) {
            const productionQty = Number(item.productionQty) || 0;
            const rejectQty = Number(item.rejectQty) || 0;
            daysData[dayIndex].productionQty += productionQty;
            daysData[dayIndex].rejectQty += rejectQty;
          }
        } catch (error) {
          // console.error('Error processing blanking date:', item.productionDate, error);
        }
      }
    });

    return daysData;
  }, [reportData]);

  // X-axis label step to avoid overlapping labels
  const blankingXAxisLabelStep = useMemo(() => {
    const length = blankingChartData.length || 0;
    if (length <= 15) return 1;
    return Math.ceil(length / 12);
  }, [blankingChartData]);

  const downloadCsv = (filename: string, headers: string[], rows: string[][]) => {
    const csvRows = [
      headers.join(','),
      ...rows.map((row) =>
        row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(',')
      ),
    ];
    const blob = new Blob([csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleExportDetail = () => {
    if (sortedData.length === 0) {
      toast.error('No detail data to export');
      return;
    }

    try {
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
      downloadCsv(
        `Production_Detail_${timestamp}.csv`,
        [
          'Worker Name',
          'Machine Name',
          'Production Date',
          'Work Order Number',
          'GRN Number',
          'Heat No',
          'Endgood Name',
          'Process Name',
          'Work Area',
          'Production Qty',
          'Reject Qty',
        ],
        sortedData.map((item) => [
          item.workerName || '',
          item.physicalMachineName || '',
          item.productionDate || '',
          item.workOrderNumber || '',
          item.grnNumber || '',
          item.heatNo || '',
          item.endgoodName || '',
          item.processName || '',
          item.workArea || '',
          (item.productionQty ?? 0).toString(),
          (item.rejectQty ?? 0).toString(),
        ])
      );
      toast.success(`Exported ${sortedData.length} detail records to CSV`);
    } catch (error) {
      toast.error('Failed to export detail CSV');
    }
  };

  const handleExportSummary = () => {
    if (summaryData.length === 0) {
      toast.error('No summary data to export');
      return;
    }

    try {
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
      downloadCsv(
        `Production_Summary_${timestamp}.csv`,
        [
          'Production Date',
          'Endgood Name',
          'Process Name',
          'Production Qty',
          'Reject Qty',
        ],
        summaryData.map((item) => [
          item.productionDate || '',
          item.endgoodName || '',
          item.processName || '',
          (item.productionQty ?? 0).toString(),
          (item.rejectQty ?? 0).toString(),
        ])
      );
      toast.success(`Exported ${summaryData.length} summary records to CSV`);
    } catch (error) {
      toast.error('Failed to export summary CSV');
    }
  };


  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-primary-100/50 p-6">
      <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/reports')}
            className="text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Reports
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Production Report</h1>
            <p className="text-gray-600 mt-1">Production summary by branch and date range</p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <Card className="border-primary-200 shadow-sm">
        <CardHeader className="bg-gradient-to-r from-primary-50 to-primary-100/50">
          <CardTitle className="text-lg font-semibold flex items-center gap-2">
            <Calendar className="h-5 w-5 text-primary-600" />
            Date Range Selection
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Search first */}
            <div>
              <Label htmlFor="search" className="mb-2 block">Search</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <Input
                  id="search"
                  placeholder="Search by Endgood Name or Work Order Number..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 border-primary-200 focus:border-primary-400 focus:ring-primary-400"
                />
              </div>
            </div>

            {/* Date range next */}
            <div>
              <Label htmlFor="startDate" className="mb-2 block">Start Date *</Label>
              <Input
                id="startDate"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="border-primary-200 focus:border-primary-400 focus:ring-primary-400"
              />
            </div>
            <div>
              <Label htmlFor="endDate" className="mb-2 block">End Date *</Label>
              <Input
                id="endDate"
                type="date"
                value={endDate}
                max={getTodayDate()}
                onChange={(e) => {
                  const selectedDate = e.target.value;
                  const today = getTodayDate();
                  // Only set the date if it's not greater than today
                  if (selectedDate <= today) {
                    setEndDate(selectedDate);
                  } else {
                    // If user tries to select a future date, set it to today
                    setEndDate(today);
                    toast.error('End date cannot be more than today');
                  }
                }}
                className="border-primary-200 focus:border-primary-400 focus:ring-primary-400"
              />
            </div>

            {/* Load button last */}
            <div className="flex items-end">
              <Button
                onClick={loadReportData}
                disabled={loading || !startDate || !endDate}
                className="w-full bg-primary-600 hover:bg-primary-700 text-white"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Loading...
                  </>
                ) : (
                  'Load Report'
                )}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Report Data Table */}
      {loading && (
        <Card>
          <CardContent className="py-12">
            <div className="flex items-center justify-center">
              <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
              <span className="ml-3 text-gray-600">Loading production report...</span>
            </div>
          </CardContent>
        </Card>
      )}

      {!loading && sortedData.length > 0 && (
        <>
          {/* Blanking Process Chart */}
          {blankingChartData.length > 0 && (
            <Card className="border-primary-200 shadow-sm">
              <CardHeader className="bg-gradient-to-r from-primary-50 to-primary-100/50">
                <CardTitle className="text-lg font-semibold">
                  Blanking Process - Production & Reject Quantity by Date
                  {startDate && endDate && (
                    <span className="text-sm font-normal text-gray-600 ml-2">
                      - {new Date(startDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} to {new Date(endDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                    </span>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-6">
                <div className="w-full">
                  <ResponsiveContainer width="100%" height={400}>
                    <BarChart
                      data={blankingChartData}
                      margin={{
                        top: 20,
                        right: 30,
                        left: 20,
                        bottom: 5,
                      }}
                      barCategoryGap="20%"
                      barGap={0}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis 
                        dataKey="date" 
                        tick={{ fontSize: 11 }}
                        axisLine={{ stroke: '#e0e0e0' }}
                        tickLine={{ stroke: '#e0e0e0' }}
                        interval={0}
                        minTickGap={0}
                        tickFormatter={(value, index) => (index % blankingXAxisLabelStep === 0 ? value : '')}
                      />
                      <YAxis 
                        label={{ value: 'Quantity', angle: -90, position: 'insideLeft' }}
                        tick={{ fontSize: 12 }}
                        axisLine={{ stroke: '#e0e0e0' }}
                        tickLine={{ stroke: '#e0e0e0' }}
                        tickFormatter={(value) => formatNumber(value)}
                      />
                      <Tooltip 
                        formatter={(value: number, name: string) => [formatNumber(value), name]}
                        labelFormatter={(label, payload) => {
                          if (payload && payload[0] && payload[0].payload) {
                            return payload[0].payload.fullDate;
                          }
                          return label;
                        }}
                        contentStyle={{
                          backgroundColor: 'white',
                          border: '1px solid #e0e0e0',
                          borderRadius: '6px'
                        }}
                      />
                      <Legend />
                      <Bar 
                        dataKey="productionQty" 
                        fill="#3b82f6" 
                        name="Production Qty"
                        radius={[4, 4, 0, 0]}
                      >
                        {blankingChartData.map((entry, index) => (
                          <Cell
                            key={`cell-production-${index}`}
                            fill="#3b82f6"
                          />
                        ))}
                      </Bar>
                      <Bar 
                        dataKey="rejectQty" 
                        fill="#ef4444" 
                        name="Reject Qty"
                        radius={[4, 4, 0, 0]}
                      >
                        {blankingChartData.map((entry, index) => (
                          <Cell
                            key={`cell-reject-${index}`}
                            fill="#ef4444"
                          />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                
                {/* Summary Stats */}
                {(() => {
                  const totalProduction = blankingChartData.reduce((sum, day) => sum + day.productionQty, 0);
                  const totalReject = blankingChartData.reduce((sum, day) => sum + day.rejectQty, 0);
                  
                  return (
                    <div className="border-t border-gray-200 pt-4 mt-4">
                      <div className="flex items-center justify-center gap-8">
                        <div className="text-center">
                          <div className="text-sm text-gray-600">Total Production Qty</div>
                          <div className="text-2xl font-bold" style={{ color: '#3b82f6' }}>
                            {formatNumber(totalProduction)}
                          </div>
                        </div>
                        <div className="text-center">
                          <div className="text-sm text-gray-600">Total Reject Qty</div>
                          <div className="text-2xl font-bold" style={{ color: '#ef4444' }}>
                            {formatNumber(totalReject)}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })()}
              </CardContent>
            </Card>
          )}

          {/* Aggregated Summary Table */}
          {summaryData.length > 0 && (
            <Card className="border-primary-200 shadow-sm">
              <CardHeader className="bg-gradient-to-r from-primary-50 to-primary-100/50">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg font-semibold">
                    Production Summary
                    <span className="text-sm font-normal text-gray-600 ml-2">
                      ({summaryData.length} records)
                    </span>
                  </CardTitle>
                  <Button
                    onClick={handleExportSummary}
                    variant="outline"
                    size="sm"
                    className="border-primary-200 text-primary-600 hover:bg-primary-50"
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Export Summary CSV
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="pt-6">
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-gradient-to-r from-primary-50 to-primary-100/50">
                        <TableHead>Production Date</TableHead>
                        <TableHead>Endgood Name</TableHead>
                        <TableHead>Process Name</TableHead>
                        <TableHead className="text-right">Production Qty</TableHead>
                        <TableHead className="text-right">Reject Qty</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {summaryData.map((item, index) => (
                        <TableRow key={index} className="hover:bg-primary-50/50">
                          <TableCell>
                            {item.productionDate
                              ? new Date(item.productionDate).toLocaleDateString('en-IN', {
                                  year: 'numeric',
                                  month: 'short',
                                  day: 'numeric',
                                })
                              : 'N/A'}
                          </TableCell>
                          <TableCell>{item.endgoodName || 'N/A'}</TableCell>
                          <TableCell>{item.processName || 'N/A'}</TableCell>
                          <TableCell className="text-right">{formatNumber(item.productionQty)}</TableCell>
                          <TableCell className="text-right text-red-600">{formatNumber(item.rejectQty)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          )}

          <Card className="border-primary-200 shadow-sm">
            <CardHeader className="bg-gradient-to-r from-primary-50 to-primary-100/50">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg font-semibold">
                  Production Detail Data
                </CardTitle>
                <div className="flex items-center gap-4">
                  <Button
                    onClick={handleExportDetail}
                    variant="outline"
                    size="sm"
                    className="border-primary-200 text-primary-600 hover:bg-primary-50"
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Export Detail CSV
                  </Button>
                  <div className="flex items-center gap-2">
                    <Label className="text-sm text-gray-600">Items per page:</Label>
                    <Select value={itemsPerPage.toString()} onValueChange={handleItemsPerPageChange}>
                      <SelectTrigger className="w-20 h-8">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {paginationConfig.itemsPerPageOptions.map((option) => (
                          <SelectItem key={option} value={option.toString()}>
                            {option}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <span className="text-sm text-gray-600">
                    Showing {startIndex + 1} - {Math.min(endIndex, sortedData.length)} of {sortedData.length} records
                  </span>
                </div>
              </div>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gradient-to-r from-primary-50 to-primary-100/50">
                      <TableHead 
                        className="cursor-pointer hover:bg-primary-100/50"
                        onClick={() => handleSort('workerName')}
                      >
                        <div className="flex items-center gap-2">
                          Worker Name
                          {getSortIcon('workerName')}
                        </div>
                      </TableHead>
                      <TableHead 
                        className="cursor-pointer hover:bg-primary-100/50"
                        onClick={() => handleSort('physicalMachineName')}
                      >
                        <div className="flex items-center gap-2">
                          Machine Name
                          {getSortIcon('physicalMachineName')}
                        </div>
                      </TableHead>
                      <TableHead 
                        className="cursor-pointer hover:bg-primary-100/50"
                        onClick={() => handleSort('productionDate')}
                      >
                        <div className="flex items-center gap-2">
                          Production Date
                          {getSortIcon('productionDate')}
                        </div>
                      </TableHead>
                      <TableHead 
                        className="cursor-pointer hover:bg-primary-100/50"
                        onClick={() => handleSort('workOrderNumber')}
                      >
                        <div className="flex items-center gap-2">
                          Work Order Number
                          {getSortIcon('workOrderNumber')}
                        </div>
                      </TableHead>
                      <TableHead 
                        className="cursor-pointer hover:bg-primary-100/50"
                        onClick={() => handleSort('grnNumber')}
                      >
                        <div className="flex items-center gap-2">
                          GRN Number
                          {getSortIcon('grnNumber')}
                        </div>
                      </TableHead>
                      <TableHead 
                        className="cursor-pointer hover:bg-primary-100/50"
                        onClick={() => handleSort('heatNo')}
                      >
                        <div className="flex items-center gap-2">
                          Heat No
                          {getSortIcon('heatNo')}
                        </div>
                      </TableHead>
                      <TableHead 
                        className="cursor-pointer hover:bg-primary-100/50"
                        onClick={() => handleSort('endgoodName')}
                      >
                        <div className="flex items-center gap-2">
                          Endgood Name
                          {getSortIcon('endgoodName')}
                        </div>
                      </TableHead>
                      <TableHead 
                        className="cursor-pointer hover:bg-primary-100/50"
                        onClick={() => handleSort('processName')}
                      >
                        <div className="flex items-center gap-2">
                          Process Name
                          {getSortIcon('processName')}
                        </div>
                      </TableHead>
                      <TableHead 
                        className="cursor-pointer hover:bg-primary-100/50"
                        onClick={() => handleSort('workArea')}
                      >
                        <div className="flex items-center gap-2">
                          Work Area
                          {getSortIcon('workArea')}
                        </div>
                      </TableHead>
                      <TableHead 
                        className="cursor-pointer hover:bg-primary-100/50 text-right"
                        onClick={() => handleSort('productionQty')}
                      >
                        <div className="flex items-center justify-end gap-2">
                          Production Qty
                          {getSortIcon('productionQty')}
                        </div>
                      </TableHead>
                      <TableHead 
                        className="cursor-pointer hover:bg-primary-100/50 text-right"
                        onClick={() => handleSort('rejectQty')}
                      >
                        <div className="flex items-center justify-end gap-2">
                          Reject Qty
                          {getSortIcon('rejectQty')}
                        </div>
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {paginatedData.map((item, index) => (
                      <TableRow key={index} className="hover:bg-primary-50/50">
                        <TableCell className="font-medium">{item.workerName || 'N/A'}</TableCell>
                        <TableCell>{item.physicalMachineName || 'N/A'}</TableCell>
                        <TableCell>
                          {item.productionDate 
                            ? new Date(item.productionDate).toLocaleDateString('en-IN', { 
                                year: 'numeric', 
                                month: 'short', 
                                day: 'numeric' 
                              })
                            : 'N/A'}
                        </TableCell>
                        <TableCell>
                          {item.workOrderId && item.workOrderNumber ? (
                            <button
                              onClick={() => window.open(`/work-orders/${item.workOrderId}/view`, '_blank')}
                              className="text-blue-600 hover:text-blue-800 hover:underline cursor-pointer"
                            >
                              {item.workOrderNumber}
                            </button>
                          ) : (
                            item.workOrderNumber || 'N/A'
                          )}
                        </TableCell>
                        <TableCell>{item.grnNumber || 'N/A'}</TableCell>
                        <TableCell>{item.heatNo || 'N/A'}</TableCell>
                        <TableCell>{item.endgoodName || 'N/A'}</TableCell>
                        <TableCell>{item.processName || 'N/A'}</TableCell>
                        <TableCell>{item.workArea || 'N/A'}</TableCell>
                        <TableCell className="text-right">{formatNumber(item.productionQty)}</TableCell>
                        <TableCell className="text-right text-red-600">{formatNumber(item.rejectQty)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center">
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={handlePageChange}
              />
            </div>
          )}
        </>
      )}

      {!loading && sortedData.length === 0 && (
        <Card>
          <CardContent className="py-12">
            <div className="text-center text-gray-500">
              <Calendar className="h-12 w-12 mx-auto mb-3 text-gray-400" />
              <p>No data available. Please select a date range and load the report.</p>
            </div>
          </CardContent>
        </Card>
      )}
      </div>
    </div>
  );
};

export default ProductionReportPage;
